import { createStore } from '@/utils/create-store.ts'
import { makeApiFilters } from '@/utils/search-filters/make-api-filters.ts'
import {
  type DateValue,
  type SearchApiFilterValue,
  TimePreset,
} from '@/utils/types/filter-types.ts'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

export interface FilterValues {
  [key: string]:
    | string
    | number
    | DateValue
    | string[]
    | boolean
    | { start: number; end: number }[]
    | ((filter: Partial<FilterValues>) => void)
    | ((timePreset: TimePreset, start: DateValue, end: DateValue) => void)
    | ((sensor: string) => void)
    | ((providers: string[]) => void)
    | ((location: string, locationType: string) => void)
    | (() => void)
  timePreset: TimePreset
  customTimeStart: DateValue
  customTimeEnd: DateValue
  sensors: string[]
  providers: string[]
  quality: string
  cloudCover: number
  angle: string
  orientations: { start: number; end: number }[]
  location: string
  locationType: string
  keywords: string[]
  isFilterExpanded: boolean
  frequency: string
  formattedAddress: string
  placeId: string
  country: string
  adminAreaLevel1: string
  adminAreaLevel2: string
}

export const initialFilterState: FilterValues = {
  timePreset: TimePreset.None,
  customTimeStart: null,
  customTimeEnd: null,
  sensors: [],
  providers: [],
  quality: '5000',
  cloudCover: 100,
  angle: '50',
  orientations: [],
  formattedAddress: '',
  placeId: '',
  country: '',
  adminAreaLevel1: '',
  adminAreaLevel2: '',
  location: '',
  locationType: '',
  keywords: [],
  isFilterExpanded: false,
  frequency: `DAILY(${dayjs().hour(7).minute(0).utc().format('HH:mm')})`,
}

export interface Filters extends FilterValues {
  setFilters: (filters: Partial<FilterValues>) => void
  makeSearchFiltersFromCurrentPanelValues: () => SearchApiFilterValue[]
  setTimePreset: (
    timePreset: TimePreset,
    start: DateValue,
    end: DateValue,
  ) => void
  toggleSensors: (sensor: string) => void
  setProviders: (providers: string[]) => void
  setLocation: (location: string, locationType: string) => void
  resetFilters: () => void
  toggleFilterPanel: () => void
  setEmailUpdateFrequency: (frequency: string) => void
}

export const useFiltersStore = createStore<Filters>({
  initialState: initialFilterState,
  actions: (set, get): Partial<Filters> => ({
    setFilters: (filters: Partial<Filters>) => {
      set(filters)
    },
    setTimePreset: (
      timePreset: TimePreset,
      start: DateValue,
      end: DateValue,
    ) => {
      set({
        timePreset,
        customTimeStart: start,
        customTimeEnd: end,
      })
    },
    toggleSensors: (sensor: string) => {
      const currentSensors = new Set(get().sensors)
      if (currentSensors.has(sensor)) {
        currentSensors.delete(sensor)
      } else {
        currentSensors.add(sensor)
      }
      set({ sensors: [...currentSensors] })
    },
    setProviders: (providers: string[]) => {
      set({ providers })
    },
    setLocation: (location: string, locationType: string) => {
      set({ location, locationType })
    },
    resetFilters: () => {
      set(initialFilterState)
    },
    toggleFilterPanel: () => {
      const currentState = get()
      console.log(currentState)
      set({ isFilterExpanded: !currentState.isFilterExpanded })
    },
    setEmailUpdateFrequency: (frequency: string) => {
      set({ frequency: frequency })
    },
  }),
})

export const useSetFilters = () => useFiltersStore().setFilters
export const useMakeSearchFilters = () =>
  useFiltersStore().makeSearchFiltersFromCurrentPanelValues
export const useSetTimePreset = () => useFiltersStore().setTimePreset
export const useToggleSensors = () => useFiltersStore().toggleSensors
export const useSetProviders = () => useFiltersStore().setProviders
export const useSetLocation = () => useFiltersStore().setLocation
export const useResetFilters = () => useFiltersStore().resetFilters
export const useToggleFilterPanel = () => useFiltersStore().toggleFilterPanel
export const useSetEmailUpdateFrequency = () =>
  useFiltersStore().setEmailUpdateFrequency

export const useGetFilters = () => useFiltersStore((s) => s)

export const useCloudCoverFilter = () => useGetFilters().cloudCover
export const useSensorsFilter = () => useGetFilters().sensors
export const useProvidersFilter = () => useGetFilters().providers
export const useLocationFilter = () => useGetFilters().location
export const useLocationTypeFilter = () => useGetFilters().locationType
export const useKeywordsFilter = () => useGetFilters().keywords
export const useQualityFilter = () => useGetFilters().quality
export const useAngleFilter = () => useGetFilters().angle
export const useOrientationsFilter = () => useGetFilters().orientations
export const useFrequencyFilter = () => useGetFilters().frequency
export const useIsFilterExpanded = () => useGetFilters().isFilterExpanded
export const useCustomTimeStartFilter = () => useGetFilters().customTimeStart
export const useCustomTimeEndFilter = () => useGetFilters().customTimeEnd
export const useTimePresetFilter = () => useGetFilters().timePreset
export const useEmailUpdateFrequencyFilter = () => useGetFilters().frequency

export const useMakeSearchFiltersFromCurrentPanelValues = () =>
  makeApiFilters(useGetFilters())

export const useHaveFiltersUpdated = () => {
  const filters = useGetFilters()
  return JSON.stringify(filters) !== JSON.stringify(initialFilterState)
}
export const useActiveFilters = () => {
  const filters = useGetFilters()
  return Object.keys(filters).reduce((count, key) => {
    if (Array.isArray(filters[key])) {
      const array = filters[key] as Array<string>
      return array.length > 0 ? count + 1 : count
    } else {
      return filters[key] === initialFilterState[key] ? count : count + 1
    }
  }, 0)
}
